import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserCircle2 } from "lucide-react";
import { COMPANY, NAV_ITEMS, APPLY_PATH, LOGO_URL } from "@/data/content";
import { useAuth } from "@/auth/AuthContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const dashPath = user?.role === "admin" ? "/admin" : user?.partner_id ? "/portal" : user?.investor_stage || user?.investor_access ? "/investors/portal" : "/account";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") navigate(`/#${hash}`);
    else {
      const el = document.getElementById(hash);
      if (el) window.__lenis?.scrollTo(el, { offset: -88, duration: 1.3 });
      else navigate(`/#${hash}`);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "glass border-b border-black/[0.06]" : "border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-[92px] items-center justify-between gap-4">
        <Link to="/" className="group flex items-center" data-testid="nav-logo" aria-label="Xtreme Exotics Watersports home">
          <img
            src={LOGO_URL}
            alt="Xtreme Exotics Watersports"
            className="h-[68px] w-auto drop-shadow-[0_0_12px_rgba(0,252,245,0.28)] transition-[filter,transform] duration-500 group-hover:drop-shadow-[0_0_22px_rgba(0,252,245,0.55)]"
          />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.hash}
              onClick={() => goSection(item.hash)}
              data-testid={`nav-${item.hash}`}
              className="text-[13px] font-semibold tracking-wide text-muted transition-colors duration-300 hover:text-aqua"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone"
            className="hidden font-mono text-xs tracking-widest text-muted transition-colors hover:text-aqua lg:block"
          >
            {COMPANY.phone}
          </a>
          <Link
            to={user ? dashPath : "/login"}
            data-testid="nav-signin-btn"
            aria-label={user ? "My account" : "Sign in"}
            className="grid h-10 w-10 place-items-center rounded-full border border-black/10 text-muted transition-colors duration-300 hover:border-aqua/50 hover:text-aqua"
          >
            <UserCircle2 size={18} />
          </Link>
          <Link to={APPLY_PATH} className="btn-primary hidden !px-6 !py-2.5 !text-xs sm:inline-flex" data-testid="nav-apply-btn">
            Apply Now
          </Link>
          <button
            className="grid h-10 w-10 place-items-center rounded-md border border-black/10 text-offwhite xl:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-btn"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="glass border-t border-black/[0.06] xl:hidden" aria-label="Mobile">
          <div className="container-x flex flex-col gap-1 py-4">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.hash}
                onClick={() => goSection(item.hash)}
                data-testid={`nav-mobile-${item.hash}`}
                className="rounded-md px-3 py-3 text-left text-sm font-semibold text-offwhite transition-colors hover:bg-black/5 hover:text-aqua"
              >
                {item.label}
              </button>
            ))}
            <Link to={APPLY_PATH} onClick={() => setOpen(false)} className="btn-primary mt-3" data-testid="nav-mobile-apply-btn">
              Apply Now
            </Link>
            <Link
              to={user ? dashPath : "/login"}
              onClick={() => setOpen(false)}
              className="btn-ghost mt-2"
              data-testid="nav-mobile-signin-btn"
            >
              {user ? "My Account" : "Sign In"}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
